// src/api/index.js
import axios from "axios";
import { uploadUrl, upload } from "./uploadConfig.js";
import { toBrowsablePublisherImageUrl } from "../utils/newsImageUtils";
//ggttff
// API URL based on current hostname - dynamically calculated at request time
const getBaseUrl = () => {
  // Prefer build-time override when provided (useful for live deployments)
  // Safety: never allow a production host to accidentally use a localhost base URL.
  const envBaseUrl = String(import.meta?.env?.VITE_API_BASE_URL || "").trim();
  if (envBaseUrl) {
    if (typeof window !== 'undefined') {
      const hostname = window.location?.hostname;
      const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1';
      const isLocalOverride = /(^|\/\/)(localhost|127\.0\.0\.1)(:|\/|$)/i.test(envBaseUrl);
      if (!isLocalHost && isLocalOverride) {
        // Ignore unsafe override and fall back to hostname-based routing.
      } else {
        return envBaseUrl;
      }
    } else {
      return envBaseUrl;
    }
  }

  // Ensure window is available
  if (typeof window === 'undefined') {
    return "http://localhost:3002/api";
  } 
  
  const { hostname, protocol, port, origin } = window.location;
  
  // Handle localhost development (Vite dev server on port 5173)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Backend API runs on port 3002
    return "http://localhost:3002/api";
  }
  
  // Handle unmaapp.com domains - always use www.unmaapp.com for consistency
  if (hostname === 'unmaapp.com' || hostname === 'www.unmaapp.com' || hostname.includes('unmaapp.com')) {
    // Always use https://www.unmaapp.com for production to avoid redirects and CORS issues
    return "https://www.unmaapp.com/api";
  }
  if (hostname === 'newsworld.testatozas.in' || hostname.includes('testatozas.in')) {
    return "https://newsworld.testatozas.in/api";
  }
  
  // For unknown hosts/IPs:
  // - Vite dev server (5173) => call backend on same host at 3002
  // - Otherwise => use same-origin /api (for reverse-proxy deployments)
  if (port === '5173') {
    return `${protocol}//${hostname}:3002/api`;
  }
  return `${origin}/api`;
};

const getInitialBaseUrl = () => getBaseUrl();

const API = axios.create({
  baseURL: getInitialBaseUrl(),
  timeout: 15000, // 15 seconds timeout
});

// Local news aggregation can be slower because it fans out to external sources.
const LOCAL_NEWS_TIMEOUT_MS = 45000;
const GEOGRAPHY_TIMEOUT_MS = 90000;
//fggr
// Request interceptor to dynamically set baseURL and throttle requests
let lastRequestTime = 0;
const MIN_REQUEST_INTERVAL = 250;

// Long timeout for upload endpoints (contribute/edit with video/media)
const UPLOAD_TIMEOUT_MS = 25 * 60 * 1000; // 25 minutes (large video on slow connections)

API.interceptors.request.use(
  (config) => {
    // Dynamically update baseURL before each request to ensure it's always correct
    // This fixes the issue where baseURL might be stale after page load
    const currentBaseUrl = getBaseUrl();
    // Always set baseURL to ensure it's correct (handles initial load and hostname changes)
    config.baseURL = currentBaseUrl;

    // Force long timeout and ensure correct Content-Type for FormData (video/media uploads)
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      config.timeout = UPLOAD_TIMEOUT_MS;
      // Let the browser set Content-Type with multipart boundary (required for file upload)
      delete config.headers['Content-Type'];
    } else if (config.url === '/contribute' || (config.url && config.url.startsWith('/edit/'))) {
      config.timeout = UPLOAD_TIMEOUT_MS;
    } else if (config.url && (config.url.startsWith('/news/local') || config.url.includes('/news/article-image'))) {
      config.timeout = LOCAL_NEWS_TIMEOUT_MS;
    } else if (config.url && config.url.startsWith('/sources/')) {
      // Source detail + articles can include soft RSS refresh; allow more headroom.
      config.timeout = Math.max(config.timeout || 0, 45000);
    } else if (config.url && config.url.startsWith('/geography/')) {
      config.timeout = GEOGRAPHY_TIMEOUT_MS;
    }
    
    // Automatically add Authorization header from localStorage if available
    // Only add if not already set (allows manual override)
    if (!config.headers.Authorization && typeof window !== 'undefined') {
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    
    // Log in development or if baseURL changed
    if (import.meta.env.DEV) {
      console.log('[API] Request to:', config.url, 'with baseURL:', currentBaseUrl);
    }
    
    // Only throttle GET requests to news endpoints (not per-article image lookups)
    const isArticleImageLookup = config.url?.includes('/news/article-image');
    const isNewsEndpoint =
      !isArticleImageLookup &&
      (config.url?.includes('/category/') || config.url?.includes('/news'));
    
    if (config.method === 'get' && isNewsEndpoint) {
      // Add delay if requests are too frequent
      const now = Date.now();
      const timeSinceLastRequest = now - lastRequestTime;
      
      if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
        const delay = MIN_REQUEST_INTERVAL - timeSinceLastRequest;
        return new Promise((resolve) => {
          setTimeout(() => {
            lastRequestTime = Date.now();
            resolve(config);
          }, delay);
        });
      }
      
      lastRequestTime = now;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token expiration and 429 errors
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const isNewsEndpoint = error.config?.url?.includes('/category/') || error.config?.url?.includes('/news');
    const isNetworkOrTimeout = error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK' || error.message === 'Network Error';
    const isTimeout = error.code === 'ECONNABORTED';

    if (isNewsEndpoint && (error.response?.status === 429 || isNetworkOrTimeout)) {
      const message = error.response?.status === 429
        ? "Too many requests. Please wait 10 seconds before refreshing."
        : isTimeout
          ? "Local news is taking longer than expected. Please try again in a moment."
          : "Could not reach the server. Ensure the backend is running (e.g. node server.js on port 3002).";
      console.warn('[Axios] News request failed - returning empty results:', error.code || error.response?.status, message);

      return Promise.resolve({
        data: {
          success: true,
          results: [],
          totalResults: 0,
          nextPage: null,
          message,
        },
        status: 200,
        statusText: 'OK',
        headers: error.response?.headers || {},
        config: error.config,
      });
    }
    
    // Check if error is 401 Unauthorized
    if (error.response?.status === 401) {
      const errorMessage = error.response?.data?.message || "";
      
      // Check if token has expired or is invalid
      if (errorMessage === "Token has expired" || 
          errorMessage.includes("expired") || 
          errorMessage === "Invalid token" ||
          errorMessage === "Access token is required" ||
          errorMessage === "Token verification failed") {
        // Remove token and user from localStorage
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        
        // Dispatch custom event to notify AuthContext
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event('authTokenCleared'));
        }
        
        // Note: Redirect is handled by React Router in components, not here
        // This avoids hard refresh and allows React Router to handle navigation smoothly
      }
    }
    
    // Return the error so it can be handled by the calling code
    return Promise.reject(error);
  }
);
  
// Reverse geocode coordinates to location
export const reverseGeocode = async (latitude, longitude) => {
  const response = await API.post('/news/geocode', { latitude, longitude });
  return response.data;
};
// Forward geocode: place name (e.g. "Kadaba", "Kodagu, Karnataka") to location for manual place search
export const forwardGeocodePlace = async (place) => {
  const response = await API.post('/news/geocode/place', { place });
  return response.data;
};
const extractLocalNewsList = (payload = {}) => {
  if (Array.isArray(payload?.articles)) return payload.articles;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};
const INVALID_LOCAL_NEWS_URL_PATTERN = /\/cdn-cgi\/l\/email-protection(?:[/?#]|$)/i;
const INVALID_LOCAL_NEWS_SCHEME_PATTERN = /^(?:mailto:|javascript:|tel:|sms:)/i;
const INVALID_LOCAL_NEWS_PATH_PATTERN = /\/(?:404|error|news-not-found|not-found)(?:[/?#._-]|$)/i;

const sanitizeLocalNewsUrl = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw || raw === '#' || raw.startsWith('#')) return '';
  if (INVALID_LOCAL_NEWS_SCHEME_PATTERN.test(raw)) return '';
  if (INVALID_LOCAL_NEWS_URL_PATTERN.test(raw.toLowerCase())) return '';

  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const parsed = new URL(raw, base);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return '';
    if (INVALID_LOCAL_NEWS_URL_PATTERN.test(parsed.pathname.toLowerCase())) return '';
    if (INVALID_LOCAL_NEWS_PATH_PATTERN.test(parsed.pathname.toLowerCase())) return '';
    return parsed.href;
  } catch {
    return '';
  }
};

const NEWS_KADABA_DEFAULT_IMAGE =
  'https://newskadaba.com/wp-content/uploads/2022/12/News-Kadaba-Defualt-Image-copy.jpg';

const normalizeLocalNewsItem = (item = {}, language = 'en') => {
  const sourceName =
    (typeof item?.source_name === 'string' && item.source_name.trim()) ||
    (typeof item?.source === 'string' && item.source.trim()) ||
    item?.source?.name ||
    item?.author ||
    'Local Source';
  const publishedAt = item?.publishedAt || item?.pubDate || item?.createdAt || null;
  let image = item?.image_url || item?.imageUrl || item?.image || null;
  const link = sanitizeLocalNewsUrl(item?.url || item?.link || '');
  // News Kadaba posts without a story photo still ship the site default banner.
  if (!image && /newskadaba\.com/i.test(link || '')) {
    image = NEWS_KADABA_DEFAULT_IMAGE;
  }
  if (image && /newskadaba\.com/i.test(String(image))) {
    image = toBrowsablePublisherImageUrl(image) || image;
  }
  const id =
    item?._id ||
    item?.id ||
    link ||
    `${item?.title || 'local-news'}-${publishedAt || item?.time || ''}`;
  return {
    ...item,
    _id: id,
    id,
    title: item?.title || '',
    description: item?.description || item?.summary || item?.content || '',
    content: item?.content || item?.description || item?.summary || '',
    source_name: sourceName,
    source: item?.source || sourceName,
    source_id: item?.source_id || sourceName,
    image_url: image,
    image: image,
    url: link,
    link,
    publishedAt,
    pubDate: item?.pubDate || publishedAt,
    language: item?.language || language,
    category: item?.category || 'local',
  };
};
const normalizeLocalNewsResponse = (payload = {}, language = 'en') => {
  const list = extractLocalNewsList(payload).map((item) => normalizeLocalNewsItem(item, language));
  const numericTotal = Number(payload?.totalResults);
  return {
    ...payload,
    success: payload?.success !== false,
    articles: list,
    results: list,
    totalResults: Number.isFinite(numericTotal) ? numericTotal : list.length,
    nextPage: payload?.nextPage ?? payload?.next_page ?? null,
  };
};
// Fetch local news by location (country, state, district, taluk, city - RSS + AI)
export const fetchLocalNews = async (country, state = '', city = '', language = 'en', page = 1, district = '', taluk = '', options = {}) => {
  const params = { country, language, page };
  if (state) params.state = state;
  if (city) params.city = city;
  if (district) params.district = district;
  if (taluk) params.taluk = taluk;
  if (options?.exactOnly) params.exactOnly = 'true';
  if (options?.withFallback) params.withFallback = 'true';
  const response = await API.get('/news/local', { params });
  const normalizedResponse = normalizeLocalNewsResponse(response?.data || {}, language);
  if ((normalizedResponse.articles || []).length > 0) {
    return normalizedResponse;
  }
  const timeoutOrNetworkMessage = String(normalizedResponse?.message || '').toLowerCase();
  const shouldUseFallbackFeed =
    !options?.exactOnly &&
    !options?.withFallback &&
    (
      timeoutOrNetworkMessage.includes('taking longer than expected') ||
      timeoutOrNetworkMessage.includes('could not reach the server') ||
      timeoutOrNetworkMessage.includes('request in progress')
    );
  if (!shouldUseFallbackFeed) {
    return normalizedResponse;
  }
  try {
    const fallbackResponse = await API.get('/local-news');
    const fallbackNormalized = normalizeLocalNewsResponse(fallbackResponse?.data || {}, language);
    if ((fallbackNormalized.articles || []).length > 0) {
      return {
        ...fallbackNormalized,
        fromFallback: true,
        message:
          normalizedResponse?.message ||
          'Showing fallback local news while location feed is loading.',
      };
    }
  } catch {
    // Ignore fallback errors and return original response.
  }
  return normalizedResponse;
};
// Fetch local newspaper sources that cover a given location (public)
export const fetchLocalSources = async ({ country, state, district, city, taluk, language } = {}) => {
  const params = {};
  if (country) params.country = country;
  if (state) params.state = state;
  if (district) params.district = district;
  if (city) params.city = city;
  if (taluk) params.taluk = taluk;
  if (language) params.language = language;
  const response = await API.get('/news/local/sources', { params });
  return response.data;
};
export const getAuthOrigin = () => getBaseUrl().replace(/\/api\/?$/, "");

export default API;
export { uploadUrl, upload, getBaseUrl };

export const apiCall = async (url, method = "GET", data = null, config = {}) => {
  const normalizedMethod = String(method || "GET").toUpperCase();
  if (normalizedMethod === "GET") {
    const response = await API.get(url, config);
    return response.data;
  }
  const response = await API({ url, method: normalizedMethod, data, ...config });
  return response.data;
};














